import React from 'react';
import { Printer } from 'lucide-react';
import { Button } from '../../ui/button';

/**
 * SummaryPrintExport - Generates a print-friendly summary report
 * Extracted from AdminWeekendsTab.js (Phase 3H)
 * 
 * Props:
 * - summaryData: Object containing event summary data from API
 * - disabled: Boolean to disable the button
 */
export default function SummaryPrintExport({ summaryData, disabled = false }) {
  const handlePrint = () => {
    if (!summaryData) return;
    
    // Create a print-friendly version
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the summary');
      return;
    }
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Booking Summary - ${summaryData?.event_name || 'Event'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 1000px; margin: 0 auto; }
          h1 { color: #059669; border-bottom: 2px solid #059669; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
          .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
          .stat-box { background: #f0fdf4; padding: 15px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 28px; font-weight: bold; color: #059669; }
          .stat-label { font-size: 12px; color: #6b7280; }
          .financial-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .financial-item { padding: 10px; background: #f9fafb; border-radius: 5px; }
          .financial-label { font-size: 12px; color: #6b7280; }
          .financial-value { font-size: 18px; font-weight: 600; }
          .rooms-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px; }
          .room-box { background: #f9fafb; padding: 15px; border-radius: 8px; }
          .room-name { font-weight: 600; margin-bottom: 5px; }
          .room-price { font-size: 12px; color: #6b7280; margin-bottom: 10px; }
          .progress-bar { height: 8px; background: #e5e7eb; border-radius: 4px; overflow: hidden; margin-top: 10px; }
          .progress-fill { height: 100%; background: #059669; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f9fafb; font-weight: 600; font-size: 12px; color: #6b7280; }
          .addon-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
          .print-date { text-align: right; color: #6b7280; font-size: 12px; margin-bottom: 20px; }
          @media print { 
            body { padding: 0; }
            .stats { grid-template-columns: repeat(2, 1fr); }
            .rooms-grid { grid-template-columns: repeat(2, 1fr); }
            .financial-grid { grid-template-columns: repeat(2, 1fr); }
          }
        </style>
      </head>
      <body>
        <div class="print-date">Generated: ${new Date().toLocaleString('en-GB')}</div>
        <h1>📊 Booking Summary</h1>
        <p><strong>${summaryData?.event_name || 'Event'}</strong><br/>${summaryData?.event_dates || ''}</p>
        
        <div class="stats">
          <div class="stat-box">
            <div class="stat-value">${summaryData?.total_bookings || 0}</div>
            <div class="stat-label">Total Bookings</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${summaryData?.total_guests || 0}</div>
            <div class="stat-label">Total Guests</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${summaryData?.capacity_percentage || 0}%</div>
            <div class="stat-label">Capacity Filled</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">£${summaryData?.financials?.total_amount_paid?.toLocaleString() || 0}</div>
            <div class="stat-label">Total Received</div>
          </div>
        </div>

        <h2>💰 Financial Summary</h2>
        <div class="financial-grid">
          <div class="financial-item">
            <div class="financial-label">Total Revenue (Expected)</div>
            <div class="financial-value">£${summaryData?.financials?.total_revenue?.toLocaleString() || 0}</div>
          </div>
          <div class="financial-item">
            <div class="financial-label">Deposits Due</div>
            <div class="financial-value">£${summaryData?.financials?.total_deposits_due?.toLocaleString() || 0}</div>
          </div>
          <div class="financial-item">
            <div class="financial-label">Deposits Received</div>
            <div class="financial-value" style="color: #059669;">£${summaryData?.financials?.total_deposits_paid?.toLocaleString() || 0}</div>
          </div>
          <div class="financial-item">
            <div class="financial-label">Total Paid</div>
            <div class="financial-value" style="color: #059669;">£${summaryData?.financials?.total_amount_paid?.toLocaleString() || 0}</div>
          </div>
          <div class="financial-item">
            <div class="financial-label">Balance Outstanding</div>
            <div class="financial-value" style="color: #ea580c;">£${summaryData?.financials?.total_balance_due?.toLocaleString() || 0}</div>
          </div>
        </div>

        <h2>🛏️ Room Availability</h2>
        <div class="rooms-grid">
          ${Object.entries(summaryData?.rooms || {}).map(([type, room]) => `
            <div class="room-box">
              <div class="room-name">${room.name}</div>
              <div class="room-price">£${room.price_per_person} pp</div>
              <div>Booked: <strong>${room.booked}</strong></div>
              <div>Available: <strong style="color: ${room.available === 0 ? '#dc2626' : '#2563eb'}">${room.available}</strong></div>
              <div>Revenue: <strong>£${room.revenue?.toLocaleString()}</strong></div>
              <div class="progress-bar">
                <div class="progress-fill" style="width: ${room.capacity > 0 ? (room.booked / room.capacity) * 100 : 0}%; background: ${room.available === 0 ? '#dc2626' : '#059669'};"></div>
              </div>
            </div>
          `).join('')}
        </div>

        ${Object.keys(summaryData?.add_ons || {}).length > 0 ? `
          <h2>🚌 Add-ons</h2>
          ${Object.entries(summaryData?.add_ons || {}).map(([id, addon]) => `
            <div class="addon-row">
              <div>
                <strong>${addon.name}</strong><br/>
                <span style="font-size: 12px; color: #6b7280;">${addon.guests} guests · ${addon.booked_count} bookings</span>
              </div>
              <div style="text-align: right;">
                <strong>£${addon.revenue?.toLocaleString()}</strong>
              </div>
            </div>
          `).join('')}
        ` : ''}

        <h2>👥 Guest List (${summaryData?.guest_list?.length || 0} guests)</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Booking Ref</th>
              <th>Room Type</th>
              <th>Dietary Requirements</th>
            </tr>
          </thead>
          <tbody>
            ${(summaryData?.guest_list || []).map(guest => `
              <tr>
                <td>${guest.name || '-'}</td>
                <td style="font-family: monospace; font-size: 12px;">${guest.booking_ref || '-'}</td>
                <td>${guest.room_type || '-'}</td>
                <td>${guest.dietary || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handlePrint}
      disabled={disabled || !summaryData}
      className="print:hidden"
      data-testid="summary-print-btn"
    >
      <Printer className="w-4 h-4 mr-2" />
      Print / PDF
    </Button>
  );
}
